import React from 'react';

/** The two plates the previews use, as luminance (see CASINO_SETTINGS.preview*). */
const LIGHT_PLATE = 233;
const DARK_PLATE = 16;

/**
 * Picks the preview-plate tone that gives the artwork the most contrast.
 *
 * Not a brightness threshold: the deciding question is "against which plate does this
 * artwork stand out more", so each pixel's contrast against BOTH candidate plates is
 * summed (alpha-weighted, so a transparent logo is judged on its ink rather than its empty
 * space) and the better plate wins. A mean-brightness rule gets this wrong on the common
 * two-tone case — a bright gold mark with a heavy black outline averages out to a mid
 * grey and lands on the light plate, where the gold, which is most of what you see,
 * disappears. Judging by contrast keeps the outline's own contribution proportional to how
 * much of the mark it actually covers.
 *
 * Falls back to the caller's heuristic when the pixels can't be read — a cross-origin CDN
 * asset whose canvas is tainted (no CORS) — so it degrades safely.
 *
 * Shared by every logo preview on this page: the Website Logos tiles AND the replace
 * overlay (both the logo being replaced and the file the owner just picked), so the same
 * artwork is never previewed one way in the grid and another way in the dialog.
 */
export const useAutoFrame = (src: string, fallback: 'light' | 'dark'): 'light' | 'dark' => {
  const [frame, setFrame] = React.useState<'light' | 'dark'>(fallback);
  React.useEffect(() => {
    // Deliberately NOT reset to `fallback` when `src` changes. The plate is a background
    // the frame TRANSITIONS between, so dropping to the fallback the instant a replacement
    // arrives and correcting it a beat later reads as a wobble underneath the artwork's
    // cross-fade. The plate already in use holds until the new artwork has been measured;
    // only an image that cannot be judged falls back (below).
    if (!src) {
      setFrame(fallback);
      return undefined;
    }
    let cancelled = false;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      if (cancelled) return;
      try {
        const size = 24;
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          setFrame(fallback);
          return;
        }
        ctx.drawImage(img, 0, 0, size, size);
        const { data } = ctx.getImageData(0, 0, size, size);
        let onLight = 0;
        let onDark = 0;
        let alpha = 0;
        for (let i = 0; i < data.length; i += 4) {
          const a = data[i + 3];
          if (a === 0) continue;
          const lum = 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
          onLight += Math.abs(lum - LIGHT_PLATE) * a;
          onDark += Math.abs(lum - DARK_PLATE) * a;
          alpha += a;
        }
        // Nothing but empty space to judge — there is no better answer than the caller's.
        if (alpha === 0) {
          setFrame(fallback);
          return;
        }
        setFrame(onDark > onLight ? 'dark' : 'light');
      } catch {
        // Tainted canvas (no CORS) — no pixels to judge, so take the fallback tone.
        setFrame(fallback);
      }
    };
    // Unreadable artwork (404 / broken CDN entry) — same: the caller's hint is all there is.
    img.onerror = () => {
      if (!cancelled) setFrame(fallback);
    };
    img.src = src;
    return () => {
      cancelled = true;
    };
  }, [src, fallback]);
  return frame;
};

export default useAutoFrame;
